import React, { useEffect, useRef } from "react";
import * as THREE from "three";

/**
 * MidnightSky — a real 3D night sky rendered with Three.js.
 *
 *  • Scrolling flies the camera THROUGH a deep starfield, so near stars sweep
 *    past while distant ones barely drift. That is genuine parallax, not a
 *    flat pan.
 *  • Hovering makes the stars around the cursor blink faster and burn brighter
 *    than their surroundings. Nothing moves, so it never feels twitchy.
 *  • Clicking fires a shockwave of light that ignites stars in an expanding
 *    ring from the point of impact.
 *  • A lit, slowly rotating moon rides along, rising as the story unfolds.
 *  • Shooting stars streak across now and then.
 *
 * The starfield is one Points object driven by a custom shader, so all of the
 * twinkle, hover and shockwave maths runs on the GPU for thousands of stars.
 */

const vertexShader = `
  uniform float uTime;
  uniform vec2  uMouse;
  uniform vec2  uClick;
  uniform float uClickTime;
  uniform float uPixelRatio;
  uniform float uAspect;
  uniform float uInteractive;

  attribute float aSize;
  attribute float aPhase;
  attribute float aSpeed;
  attribute vec3  aColor;

  varying float vBright;
  varying vec3  vColor;

  void main() {
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * mv;

    // where this star lands on screen
    vec2 ndc = gl_Position.xy / max(gl_Position.w, 0.0001);

    // proximity to the cursor, corrected so the halo stays circular
    float dm = length((ndc - uMouse) * vec2(uAspect, 1.0));
    float hover = (1.0 - smoothstep(0.0, 0.30, dm)) * uInteractive;

    // base shimmer; near the cursor it blinks faster and harder
    float twinkle = 0.55 + 0.45 * sin(uTime * aSpeed * (1.0 + hover * 2.5) + aPhase);

    // a click sends a ring of light outward, flaring stars as it sweeps past
    float dt = uTime - uClickTime;
    float dc = length((ndc - uClick) * vec2(uAspect, 1.0));
    float ring = 1.0 - smoothstep(0.0, 0.16, abs(dc - dt * 1.15));
    float pulse = ring * exp(-dt * 1.1) * step(0.0, dt) * step(dt, 6.0);

    float bright = twinkle * (1.0 + hover * 1.7 + pulse * 5.0);
    vBright = bright;
    vColor  = aColor;

    float size = aSize * (1.0 + hover * 0.8 + pulse * 2.5);
    gl_PointSize = size * uPixelRatio * (260.0 / max(-mv.z, 1.0));
  }
`;

const fragmentShader = `
  varying float vBright;
  varying vec3  vColor;

  void main() {
    float d = length(gl_PointCoord - vec2(0.5));
    if (d > 0.5) discard;
    float a = smoothstep(0.5, 0.0, d);
    a *= a;
    gl_FragColor = vec4(vColor * vBright, a * clamp(vBright, 0.0, 1.4));
  }
`;

/** Paints a grey, cratered moon surface into a canvas texture. */
function makeMoonTexture() {
  const size = 512;
  const c = document.createElement("canvas");
  c.width = c.height = size;
  const ctx = c.getContext("2d");

  ctx.fillStyle = "#c8ccd4";
  ctx.fillRect(0, 0, size, size);

  // broad tonal variation (maria)
  for (let i = 0; i < 26; i++) {
    const x = Math.random() * size;
    const y = Math.random() * size;
    const r = 30 + Math.random() * 90;
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, "rgba(150,156,168,0.5)");
    g.addColorStop(1, "rgba(150,156,168,0)");
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }

  // craters with a lit rim and shaded floor
  for (let i = 0; i < 60; i++) {
    const x = Math.random() * size;
    const y = Math.random() * size;
    const r = 4 + Math.random() * 26;
    ctx.fillStyle = "rgba(232,236,244,0.55)";
    ctx.beginPath();
    ctx.arc(x - r * 0.12, y - r * 0.12, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "rgba(120,126,140,0.5)";
    ctx.beginPath();
    ctx.arc(x, y, r * 0.82, 0, Math.PI * 2);
    ctx.fill();
  }

  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** Soft radial sprite used for the moon's glow. */
function makeGlowTexture() {
  const c = document.createElement("canvas");
  c.width = c.height = 128;
  const ctx = c.getContext("2d");
  const g = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
  g.addColorStop(0, "rgba(215,225,245,0.55)");
  g.addColorStop(0.4, "rgba(170,190,230,0.18)");
  g.addColorStop(1, "rgba(150,175,220,0)");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 128, 128);
  return new THREE.CanvasTexture(c);
}

export default function MidnightSky() {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const isTouch =
      window.matchMedia("(hover: none)").matches || window.innerWidth < 768;

    // ---------- renderer ----------
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    const pixelRatio = Math.min(window.devicePixelRatio, 2);
    renderer.setPixelRatio(pixelRatio);
    renderer.setSize(window.innerWidth, window.innerHeight);
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      60,
      window.innerWidth / window.innerHeight,
      0.1,
      1200
    );
    camera.position.set(0, 0, 0);
    scene.add(camera); // so camera children (shooting stars) render

    // ---------- starfield ----------
    const COUNT = isTouch ? 1400 : 4000;
    const SPREAD_XY = 170;
    const Z_NEAR = 30;
    const Z_FAR = -760;

    const positions = new Float32Array(COUNT * 3);
    const sizes = new Float32Array(COUNT);
    const phases = new Float32Array(COUNT);
    const speeds = new Float32Array(COUNT);
    const colors = new Float32Array(COUNT * 3);

    const tintA = new THREE.Color("#e6edff"); // white
    const tintB = new THREE.Color("#93c5fd"); // sky
    const tintC = new THREE.Color("#b7c6ff"); // pale violet

    for (let i = 0; i < COUNT; i++) {
      positions[i * 3] = (Math.random() - 0.5) * SPREAD_XY * 2;
      positions[i * 3 + 1] = (Math.random() - 0.5) * SPREAD_XY * 2;
      positions[i * 3 + 2] = Z_NEAR + Math.random() * (Z_FAR - Z_NEAR);

      sizes[i] = 0.5 + Math.random() * 2.2;
      phases[i] = Math.random() * Math.PI * 2;
      speeds[i] = 0.5 + Math.random() * 2.4;

      const r = Math.random();
      const col = r < 0.68 ? tintA : r < 0.9 ? tintB : tintC;
      colors[i * 3] = col.r;
      colors[i * 3 + 1] = col.g;
      colors[i * 3 + 2] = col.b;
    }

    const starGeo = new THREE.BufferGeometry();
    starGeo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    starGeo.setAttribute("aSize", new THREE.BufferAttribute(sizes, 1));
    starGeo.setAttribute("aPhase", new THREE.BufferAttribute(phases, 1));
    starGeo.setAttribute("aSpeed", new THREE.BufferAttribute(speeds, 1));
    starGeo.setAttribute("aColor", new THREE.BufferAttribute(colors, 3));

    const starUniforms = {
      uTime: { value: 0 },
      uMouse: { value: new THREE.Vector2(99, 99) },
      uClick: { value: new THREE.Vector2(0, 0) },
      uClickTime: { value: -999 },
      uPixelRatio: { value: pixelRatio },
      uAspect: { value: window.innerWidth / window.innerHeight },
      uInteractive: { value: isTouch ? 0 : 1 },
    };

    const starMat = new THREE.ShaderMaterial({
      uniforms: starUniforms,
      vertexShader,
      fragmentShader,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
    });

    const stars = new THREE.Points(starGeo, starMat);
    scene.add(stars);

    // ---------- moon ----------
    const moonGroup = new THREE.Group();
    scene.add(moonGroup);

    const moonTex = makeMoonTexture();
    const moon = new THREE.Mesh(
      new THREE.SphereGeometry(20, 64, 64),
      new THREE.MeshStandardMaterial({ map: moonTex, roughness: 1, metalness: 0 })
    );
    moonGroup.add(moon);

    // sunlight raking across the surface gives it a real terminator
    const sun = new THREE.DirectionalLight(0xffffff, 2.6);
    sun.position.set(-70, 34, 46);
    sun.target = moon;
    moonGroup.add(sun);
    scene.add(new THREE.AmbientLight(0x2a3550, 0.5));

    const glowTex = makeGlowTexture();
    const glow = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: glowTex,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        opacity: 0.85,
      })
    );
    glow.scale.set(96, 96, 1);
    moonGroup.add(glow);

    // ---------- shooting stars (ride with the camera so they stay in view) ----------
    const shootLayer = new THREE.Group();
    shootLayer.position.z = -90;
    camera.add(shootLayer);

    const makeShooter = () => {
      const g = new THREE.BufferGeometry();
      g.setAttribute("position", new THREE.BufferAttribute(new Float32Array(6), 3));
      // white head fading to black tail (black adds nothing under additive blending)
      g.setAttribute(
        "color",
        new THREE.BufferAttribute(new Float32Array([1, 1, 1, 0, 0, 0]), 3)
      );
      const m = new THREE.LineBasicMaterial({
        vertexColors: true,
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        opacity: 0,
      });
      const line = new THREE.Line(g, m);
      line.visible = false;
      shootLayer.add(line);
      return { line, vx: 0, vy: 0, life: 0, max: 0, x: 0, y: 0 };
    };
    const shooters = prefersReduced ? [] : [makeShooter(), makeShooter(), makeShooter()];
    let nextShoot = 1.2;

    const viewBounds = () => {
      const h = Math.tan((camera.fov * Math.PI) / 360) * 90;
      return { h, w: h * camera.aspect };
    };

    // ---------- interaction ----------
    let scrollProgress = 0;
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight || 1;
      scrollProgress = Math.min(Math.max(window.scrollY / max, 0), 1);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    const mouse = { x: 0, y: 0, tx: 0, ty: 0 };
    const onMove = (e) => {
      mouse.tx = (e.clientX / window.innerWidth) * 2 - 1;
      mouse.ty = -((e.clientY / window.innerHeight) * 2 - 1);
      starUniforms.uMouse.value.set(mouse.tx, mouse.ty);
    };
    const onLeave = () => starUniforms.uMouse.value.set(99, 99);
    if (!isTouch) {
      window.addEventListener("pointermove", onMove, { passive: true });
      document.addEventListener("mouseleave", onLeave);
    }

    const onDown = (e) => {
      const x = (e.clientX / window.innerWidth) * 2 - 1;
      const y = -((e.clientY / window.innerHeight) * 2 - 1);
      starUniforms.uClick.value.set(x, y);
      starUniforms.uClickTime.value = starUniforms.uTime.value;
    };
    window.addEventListener("pointerdown", onDown, { passive: true });

    const onResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
      starUniforms.uAspect.value = camera.aspect;
    };
    window.addEventListener("resize", onResize);

    // ---------- loop ----------
    const clock = new THREE.Clock();
    let raf;
    let eased = 0;
    const TRAVEL = 520; // how deep the scroll carries us into the field

    const tick = () => {
      raf = requestAnimationFrame(tick);
      const t = clock.getElapsedTime();
      const dt = Math.min(clock.getDelta(), 0.05);
      starUniforms.uTime.value = t;

      eased += (scrollProgress - eased) * 0.055;
      mouse.x += (mouse.tx - mouse.x) * 0.05;
      mouse.y += (mouse.ty - mouse.y) * 0.05;

      // fly forward through the starfield as the story scrolls
      camera.position.z = -eased * TRAVEL;
      if (!isTouch) {
        // look around slightly, which parallaxes the whole scene
        camera.rotation.y = mouse.x * 0.1;
        camera.rotation.x = mouse.y * 0.06;
      }

      // The moon keeps its distance ahead, drifting up through the lower left.
      // It deliberately stays out of the middle of the screen: its pale grey is
      // close to the body text colour, so crossing the centre hurts readability.
      moonGroup.position.set(
        camera.position.x + (-138 + eased * 20),
        camera.position.y + (-84 + eased * 72),
        camera.position.z - 240
      );
      // the moon turns as you scroll (a full spin across the whole story),
      // plus a whisper of idle drift so it never looks frozen at rest
      moon.rotation.y = eased * Math.PI * 2 + (prefersReduced ? 0 : t * 0.012);

      // shooting stars
      if (!prefersReduced) {
        nextShoot -= dt;
        if (nextShoot <= 0) {
          const free = shooters.find((s) => !s.line.visible);
          if (free) {
            const { w, h } = viewBounds();
            const fromLeft = Math.random() < 0.5;
            free.x = fromLeft ? -w * (0.5 + Math.random() * 0.5) : w * (0.5 + Math.random() * 0.5);
            free.y = h * (0.1 + Math.random() * 0.7);
            const speed = 70 + Math.random() * 50;
            free.vx = (fromLeft ? 1 : -1) * speed;
            free.vy = -speed * (0.35 + Math.random() * 0.3);
            free.life = 0;
            free.max = 1.1 + Math.random() * 0.7;
            free.line.visible = true;
          }
          nextShoot = 1.4 + Math.random() * 2.4;
        }
        shooters.forEach((s) => {
          if (!s.line.visible) return;
          s.life += dt;
          s.x += s.vx * dt;
          s.y += s.vy * dt;
          const p = s.line.geometry.attributes.position.array;
          const tailLen = 0.16;
          p[0] = s.x;
          p[1] = s.y;
          p[2] = 0;
          p[3] = s.x - s.vx * tailLen;
          p[4] = s.y - s.vy * tailLen;
          p[5] = 0;
          s.line.geometry.attributes.position.needsUpdate = true;
          s.line.material.opacity = Math.sin((s.life / s.max) * Math.PI) * 0.95;
          if (s.life >= s.max) s.line.visible = false;
        });
      }

      renderer.render(scene, camera);
    };
    tick();

    const onVisibility = () => {
      if (document.hidden) cancelAnimationFrame(raf);
      else tick();
    };
    document.addEventListener("visibilitychange", onVisibility);

    // ---------- cleanup ----------
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("visibilitychange", onVisibility);
      starGeo.dispose();
      starMat.dispose();
      moon.geometry.dispose();
      moon.material.dispose();
      moonTex.dispose();
      glowTex.dispose();
      glow.material.dispose();
      shooters.forEach((s) => {
        s.line.geometry.dispose();
        s.line.material.dispose();
      });
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, []);

  return (
    <div
      ref={mountRef}
      aria-hidden="true"
      style={{ position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none" }}
    />
  );
}
